import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { getFormattedContentData } from '../../services/content/index'
import SharePageTile from './SharePageTile'

const PLACEHOLDER_SIZE = 10

/**
 * Carousel slider displaying items with optional autoplay and loading placeholders.
 *
 * @param {Iterable} items - Items to display in the slider
 * @param {number} speed - Autoplay scroll speed
 * @param {boolean} autoplay - Enables autoplay scrolling
 * @param {boolean} loading - Shows placeholders while loading
 * @returns {JSX.Element} Slider component
 */
function SharePageSlider (props) {
  const {
    items,
    speed,
    autoplay,
    loading,
  } = props
  const itemsSize = items.size || 0
  const itemsArray = useMemo(() => Array.from(items), [items])
  const clonedItems = useMemo(() => [...itemsArray, ...itemsArray, ...itemsArray], [itemsArray])

  const trackRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  // AUTOPLAY
  useEffect(() => {
    if (!autoplay || !trackRef.current) return undefined
    const track = trackRef.current
    const totalWidth = track.scrollWidth / 3
    let animationFrame
    let lastTimestamp = null

    track.scrollLeft = totalWidth

    const step = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp
      const delta = timestamp - lastTimestamp
      lastTimestamp = timestamp

      track.scrollLeft += (speed * delta) / 1000
      if (track.scrollLeft >= totalWidth * 1.8) {
        track.scrollLeft -= totalWidth
      }
      animationFrame = requestAnimationFrame(step)
    }

    animationFrame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(animationFrame)
  }, [autoplay, speed, itemsArray])

  // INIT CENTER
  useEffect(() => {
    const track = trackRef.current
    if (!track || itemsSize === 0 || autoplay) return

    const initScrollToMiddleItem = () => {
      const clienteWidth = track.clientWidth
      const midIndex = Math.ceil(itemsSize / 2) - 1
      const midItem = track.children[midIndex]
      const midItemOffset = midItem.offsetLeft
      const midItemWidth = midItem.offsetWidth
      const scrollOffset = (clienteWidth / 2) - midItemOffset - (midItemWidth / 2)
      track.scrollLeft = Math.abs(scrollOffset)
    }
    requestAnimationFrame(initScrollToMiddleItem)
  }, [itemsSize, autoplay])

  // INFINITE SCROLL (manual)
  useEffect(() => {
    const track = trackRef.current
    if (!track || autoplay) return undefined

    const handleScroll = () => {
      const totalWidth = track.scrollWidth
      const third = totalWidth / 3
      if (track.scrollLeft <= third * 0.5) track.scrollLeft += third
      else if (track.scrollLeft >= third * 1.5) track.scrollLeft -= third
    }

    track.addEventListener('scroll', handleScroll)
    return () => track.removeEventListener('scroll', handleScroll)
  }, [autoplay])

  // DRAG + SNAP
  useEffect(() => {
    if (autoplay || !trackRef.current) return undefined
    const track = trackRef.current
    const getPageX = (e) => e.touches?.[0]?.pageX || e.pageX

    const smoothScrollTo = (element, target, duration = 400) => {
      const start = element.scrollLeft
      const change = target - start
      const startTime = performance.now()
      const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t)

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        // eslint-disable-next-line no-param-reassign
        element.scrollLeft = start + change * easeInOutQuad(progress)
        if (progress < 1) requestAnimationFrame(animate)
      }
      requestAnimationFrame(animate)
    }

    const onUp = () => {
      if (!isDragging) return
      setIsDragging(false)

      const itemWidth = track.children[0]?.offsetWidth || 1
      const containerWidth = track.clientWidth
      const baseOffset = (containerWidth % itemWidth) / 2
      const current = track.scrollLeft
      const alignedPosition = Math.round(
        (current - baseOffset) / itemWidth,
      ) * itemWidth + baseOffset
      smoothScrollTo(track, alignedPosition, 400)
    }

    const onDown = (e) => {
      setIsDragging(true)
      setStartX(getPageX(e) - track.offsetLeft)
      setScrollLeft(track.scrollLeft)
    }

    const onMove = (e) => {
      if (!isDragging) return
      e.preventDefault()
      const x = getPageX(e) - track.offsetLeft
      const walk = (x - startX) * 1.5
      track.scrollLeft = scrollLeft - walk
    }

    track.addEventListener('mousedown', onDown)
    track.addEventListener('mousemove', onMove)
    track.addEventListener('mouseup', onUp)
    track.addEventListener('mouseleave', onUp)
    track.addEventListener('touchstart', onDown)
    track.addEventListener('touchmove', onMove)
    track.addEventListener('touchend', onUp)

    return () => {
      track.removeEventListener('mousedown', onDown)
      track.removeEventListener('mousemove', onMove)
      track.removeEventListener('mouseup', onUp)
      track.removeEventListener('mouseleave', onUp)
      track.removeEventListener('touchstart', onDown)
      track.removeEventListener('touchmove', onMove)
      track.removeEventListener('touchend', onUp)
    }
  }, [isDragging, startX, scrollLeft, autoplay])

  return (
    <div className="share-page__slider">
      <div className={`share-page__slider-tiles share-page__slider-tiles--${autoplay ? 'autoplay' : 'draggable'}`}>
        <div
          className={`share-page__slider-track share-page__slider-track--${autoplay ? 'autoplay' : 'draggable'} ${isDragging ? 'dragging' : ''}`}
          ref={trackRef}
        >
          {loading || itemsSize === 0
            ? Array(PLACEHOLDER_SIZE).fill().map((_, id) => (
                <div key={id} className="share-page__slider-item">
                  <div className="share-page__tile-placeholder" />
                </div>
            ))
            : clonedItems.map((item, idx) => (
              <div key={idx} className={`share-page__slider-item ${idx >= itemsSize ? 'share-page__slider-item--cloned' : ''}`}>
                <SharePageTile data={getFormattedContentData(item)} />
              </div>
            ))
          }
        </div>
      </div>
    </div>
  )
}

SharePageSlider.defaultProps = {
  autoplay: false,
  speed: 20,
}

export default SharePageSlider
